import { ShoppingBagIcon } from '@heroicons/react/solid'
import { useQuery, useQueryClient } from "react-query";
import { fetchOrder, getPickingList } from "../../services";
import { AdjustQuantitiesDialog } from './AdjustQuantitiesDialog';
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GENERATE_ORDER_LABEL } from "../../navigation/constants";
import {PaperClipIcon, ExclamationCircleIcon, ChevronDownIcon} from "@heroicons/react/outline";
import { RESPONSIBLE_AREA_STYLES, ERROR_STATUS_STYLES } from "../../constants/fulfillmentErrorStyles";
import { groupOrderErrors } from "../../utils/fulfillmentErrorPresentation";
import {CarrierName, FormattedDate, getSkus, Loader, ShippingIncidents} from "../../components";
import { OrderLineItem } from './OrderLineItem';
import { OrderRomaneios } from './OrderRomaneios';
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime';

import { StatusHighlighted } from '../../components/NewTable/StatusHighlighted';
import { ORDER_STATUSES, TRACKING_STATUSES_DEFINITIONS } from '../../components/NewTable/StatusHighlightedDefinitions';
import { getOrderStatusDate } from "../../utils/orderUtils";

import { StatusPill } from '../../components/BasicTable/StatusPill';
import { Button } from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../hooks/UserContext';
import { ProofOfDelivery } from '../../components/ProofOfDelivery';
import { CubboLastmileEvents } from '../../components/CubboLastmileEvents';
import { getCubboLastmileOrderEvents } from '../../services/cubboLastmileService';
import { SkeletonLoader } from '../../components/SkeletonLoader';
import { downloadPackingProofVideo, listFilesWithPrefix } from '../../services/packingProofService';
import { StockAllocationSection } from './StockAllocationSection';
import MissingStockDiagnosisSection from './MissingStockDiagnosisSection';
import { groupRemovedUnitsByProduct } from './partialFulfillRemovedUnits';

const DEFAULT_QUERY_STALE_TIME = 1000 * 60 * 5 // 5 minutes

export const ShowOrderContainer = ({
    showIncidents = true,
    showCubboLastmileEvents = false,
    showPackingAction = false,
}) => {

    let { id, warehouse_id } = useParams()
    const history = useHistory()
    const { t, i18n} = useTranslation();
    const {user} = useContext(UserContext)
    const queryClient = useQueryClient()
    const [isAdjustOpen, setIsAdjustOpen] = useState(false)
    const {
        isLoading,
        isFetching,
        isError,
        error,
        data,
    } = useQuery(['order', id], ()=>fetchOrder(id))

    // Only REM orders, only users with the role that holds
    // STOCK_UNITS_TRANSFER_TO_LOST_OR_DAMAGED. Server enforces the permission
    // and per-order store access on the actual call.
    const REM_ORDER_TYPES = ['REMOVE_STOCK', 'REMOVE_DAMAGED_STOCK', 'REMOVE_EXPIRED_STOCK']
    const canAdjustLineQuantities = useMemo(() => {
        if (!data || !user) return false
        if (!REM_ORDER_TYPES.includes(data.order_type)) return false
        if (!Array.isArray(user.roles)) return false
        return user.roles.some((r) =>
            ['ROLE_INVENTORY_SUPERVISOR', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_WH_ADMIN'].includes(r)
        )
    }, [data, user])

    const {
        isLoading: isLoadingCubboLastmileOrderEvents,
        data: cubboLastmileOrderEvents,
    } = useQuery(['cubbo-lastmile-order-events', id], () => getCubboLastmileOrderEvents(id), {
        refetchOnWindowFocus: false,
        staleTime: DEFAULT_QUERY_STALE_TIME,
        enabled: showCubboLastmileEvents
    })

    const buildStatusHighlighted = (order) => {
        const orderStatusDate = getOrderStatusDate(order)

        return (
            <div order={order}>
                <StatusHighlighted
                    className=""
                    status={data.virtual_status}
                    statuses={ORDER_STATUSES}
                />
                {!!orderStatusDate && (
                    <div className="mt-3 ml-0.5">
                        <div className="mt-2 ml-0.5 text-gray-400 text-sm">
                            {i18n.t('orders.list.at')} <FormattedDate date={orderStatusDate} shortDate={true}/>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const handleDownloadPickingList = (picking_order_id) => {
        getPickingList(picking_order_id)
    }

    const shouldShowPickingList = (status) => {
        if (data?.uses_picking_tasks) return false
        return ['processing', 'picking', 'packing'].includes(status) && user?.permissions?.pick_all_order_stocks
    }

    const orderHasShippingLabels = useMemo(() => data?.shipping_labels && data?.shipping_labels.length > 0, [data])

    const [orderPackingProofVideoObjects, setOrderPackingProofVideoObjects] = useState([])
    const [fileKeysAreDownloading, setFileKeysAreDownloading] = useState([])
    // Expanded error groups, keyed by the id of their live row. A Set in the container and not a
    // component of its own: hooks cannot live inside the .map() below, and extracting the
    // card would move the whole block for a single piece of state.
    const [expandedGroups, setExpandedGroups] = useState(() => new Set())
    const toggleGroup = id => setExpandedGroups(previous => {
        const next = new Set(previous)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
    })

    useEffect(() => {
        const fetchPackingProofVideoObjects = async () => {
            if (!orderHasShippingLabels) {
                setOrderPackingProofVideoObjects([])
                return
            }
            try {
                const objects = await listFilesWithPrefix(String(data.id) + '_')
                // console.log("orderPackingProofVideoObjects", objects)
                setOrderPackingProofVideoObjects(objects)
            } catch (error) {
                console.error("Error fetching packing proof video objects:", error)
                setOrderPackingProofVideoObjects([])
            }
        }

        fetchPackingProofVideoObjects()
    }, [orderHasShippingLabels])

    const downloadProofVideo = async (object) => {
        // Was called with object.key, so this read `"somekey".key` and the
        // guard never fired.
        if (isDownloadingPackingProofVideo(object)) {
            return false
        }
        setFileKeysAreDownloading((prev) => [...prev, object.key])
        const filename = data?.order_number + ".webm"
        try {
            await downloadPackingProofVideo(object.redirect_path, filename)
        }
        catch (error) {
            console.error("Error downloading packing proof video:", error)
            // A silent failure just flipped the row back to a link, so operators
            // clicked it again and again (Sentry sees the rage clicks).
            window.alert(i18n.t('orders.order_details.download_failed'))
        }
        setFileKeysAreDownloading((prev) => prev.filter((key) => key !== object.key))
    }

    const isDownloadingPackingProofVideo = (object) => {
        return fileKeysAreDownloading.includes(object.key)
    }

    const removedUnits = data?.removed_stock_units_for_partial_fulfillment || []
    const groupedRemoved = groupRemovedUnitsByProduct(removedUnits)

    return (
        <>
            {isLoading ? (
                <><Loader show={true}></Loader></>
            ) : isError ? (
                <>Error: {error.message}</>
            ) : (
                    <div>
                        {removedUnits.length > 0 && (
                            <div className="mb-4 px-3 py-1.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 text-center min-w-max">
                                {i18n.t('list_processing_orders.partial_fulfillment.banner_message')}
                            </div>
                        )}
                        {showPackingAction && ['processing', 'picking', 'packing'].includes(data.status) && (
                            <div className="mb-4">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    onClick={() => history.push(GENERATE_ORDER_LABEL.replace(':id', data.id).replace(':warehouse_id', warehouse_id))}
                                >
                                    {i18n.t('fulfillment_errors.history.go_to_packing')}
                                </button>
                            </div>
                        )}
                        {canAdjustLineQuantities && (
                            <div className="mb-4 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setIsAdjustOpen(true)}
                                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                >
                                    Ajustar cantidades
                                </button>
                            </div>
                        )}
                        <AdjustQuantitiesDialog
                            open={isAdjustOpen}
                            setOpen={setIsAdjustOpen}
                            order={data}
                            onApplied={() => queryClient.invalidateQueries(['order', id])}
                        />
                        <div className="">
                            <dl>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.order_number')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        #{data.order_number}
                                    </dd>
                                </div>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.tracking_number')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2 inline-flex items-center gap-1">
                                        #<CarrierName
                                            carrierName={data.shipping_method?.carrier_name}
                                            internalCarrierName={data.shipping_method?.internal_carrier_name}
                                            suffix={data.shipping_labels[0]?.shipping_number}
                                        />
                                    </dd>
                                </div>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.status')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        {buildStatusHighlighted(data)}
                                    </dd>
                                </div>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.shipping_status')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">

                                     {data.shipping_status ?
                                        <>
                                            <StatusHighlighted
                                                className=""
                                                status={data.shipping_status}
                                                statuses={TRACKING_STATUSES_DEFINITIONS}
                                            />
                                            {data.estimated_time_arrival && data.shipping_status != 'shipment_delivered' && <div className={`mt-2 text-sm ${data.is_delayed ? 'text-red-400' : ''}`}>
                                            {i18n.t("orders.list.eta")}: <FormattedRelativeTime forceDays={true} date={data.estimated_time_arrival}/>
                                            </div>}
                                            { data.last_tracking_event?.reference_timestamp &&
                                                <div className="mt-3 ml-0.5">
                                                    {(['shipment_delivered','shipment_with_incident','shipment_canceled']).includes(data.shipping_status) ?
                                                    <div className='text-gray-400 text-sm'>
                                                        <div className="inline mr-1">{i18n.t('orders.list.at')}</div>
                                                        <FormattedDate date={data.last_tracking_event.reference_timestamp} shortDate={true}/>
                                                    </div>
                                                    : null
                                                    }
                                                </div>
                                                }
                                        </>
                                        :
                                        <div className="italic"> {i18n.t('orders.list.no_information')} </div>
                                    }
                                    </dd>
                                </div>
                                <OrderRomaneios dispatches={data.shipping_dispatches} warehouseId={data.warehouse_id} />
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.creation_date')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        {new Intl.DateTimeFormat("es", {
                                            year: "numeric",
                                            month: "long",
                                            day: "2-digit",
                                            hour: 'numeric',
                                            minute: 'numeric'
                                        }).format(new Date(data.channel_created_at))}
                                    </dd>
                                </div>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.proof_of_delivery')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        <ProofOfDelivery order={data?.id} proofs={data?.proof_of_delivery} innerClassName='flex flex-col-reverse text-base' />
                                    </dd>
                                </div>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.store')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        {data.store.name}
                                    </dd>
                                </div>
                                {/*<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">Cliente</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        ---
                                    </dd>
                                    </div>*/}
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.shipping_method')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        {data.shipping_method?.shipping_name}{ data.shipping?.first_name && <br/>}
                                    </dd>
                                </div>
                                {showCubboLastmileEvents && (
                                    <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-base font-medium text-gray-500">{i18n.t("cubbo_lastmile_events.order_events")}</dt>
                                        <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                            <SkeletonLoader isLoading={isLoadingCubboLastmileOrderEvents}>
                                                <CubboLastmileEvents cubboLastmileEvents={cubboLastmileOrderEvents?.cubbo_lastmile_events} />
                                            </SkeletonLoader>
                                        </dd>
                                    </div>
                                )}
                                {showIncidents && data?.shipping_incidents.length > 0  &&
                                    <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                                                 <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.shipping_incidents')}</dt>
                                        <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                                <ShippingIncidents shippingIncidents={data?.shipping_incidents} t={t}/>
                                        </dd>
                                    </div>
                                }
                                {data.order_fulfillment_errors?.length > 0 && (
                                    <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-base font-medium text-gray-500">
                                            <div className="flex items-center gap-1">
                                                <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                                                {i18n.t('fulfillment_errors.history.title')}
                                            </div>
                                        </dt>
                                        <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                            <div className="space-y-3">
                                                {groupOrderErrors(data.order_fulfillment_errors).map(({ error, errors, repeats, since }) => {
                                                    const isActive = error.error_status === 'open' || error.error_status === 'pending_retry'
                                                    const expanded = expandedGroups.has(error.id)
                                                    return (
                                                        <div key={error.id} className={`p-3 rounded-lg border ${isActive ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border">
                                                                        {error.code}
                                                                    </span>
                                                                    {repeats > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleGroup(error.id)}
                                                                            aria-expanded={expanded}
                                                                            title={i18n.t('fulfillment_errors.history.collapsed_hint')}
                                                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                                        >
                                                                            ×{repeats}
                                                                            <ChevronDownIcon className={`h-3 w-3 transition-transform transform ${expanded ? 'rotate-180' : ''}`} />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ERROR_STATUS_STYLES[error.error_status] || 'bg-gray-100 text-gray-800'}`}>
                                                                    {i18n.t(`fulfillment_errors.error_status.${error.error_status}`, { defaultValue: error.error_status })}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-700 mb-2">
                                                                {i18n.t(`fulfillment_errors.error_codes.${error.code}`, { defaultValue: error.error_message })}
                                                            </p>
                                                            <div className="flex flex-wrap gap-2 text-xs">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${RESPONSIBLE_AREA_STYLES[error.responsible_area] || 'bg-gray-100 text-gray-800'}`}>
                                                                    {i18n.t(`fulfillment_errors.responsible_area.${error.responsible_area}`, { defaultValue: error.responsible_area })}
                                                                </span>
                                                                <span className="text-gray-400">
                                                                    {i18n.t(`fulfillment_errors.category.${error.category}`, { defaultValue: error.category })}
                                                                </span>
                                                                {error.resolution && (
                                                                    <span className="text-green-600">
                                                                        {i18n.t(`fulfillment_errors.resolution.${error.resolution}`, { defaultValue: error.resolution })}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="mt-1 text-xs text-gray-400 flex flex-wrap items-center gap-x-2">
                                                                <FormattedDate date={error.created_at} shortDate={true} />
                                                                {repeats > 1 && (
                                                                    <>
                                                                        <span>·</span>
                                                                        {/* The range is the one thing the ×N chip cannot say: when the episode started. */}
                                                                        {/* Each half in its own span: `gap` only separates flex ELEMENTS, and with
                                                                            the label as a loose text node it came out glued as "desde10/14/26". */}
                                                                        <span className="flex items-center gap-1">
                                                                            <span>{i18n.t('fulfillment_errors.history.since')}</span>
                                                                            <span><FormattedDate date={since} shortDate={true} /></span>
                                                                        </span>
                                                                    </>
                                                                )}
                                                                {error.retry_count > 0 && (
                                                                    <>
                                                                        <span>·</span>
                                                                        <span>{i18n.t('fulfillment_errors.history.retry_count', { count: error.retry_count })}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {expanded && repeats > 1 && (
                                                                <ul className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                                                    {errors.map(round => (
                                                                        <li key={round.id} className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                                                                            <span className="tabular-nums">
                                                                                <FormattedDate date={round.created_at} shortDate={true} />
                                                                            </span>
                                                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${ERROR_STATUS_STYLES[round.error_status] || 'bg-gray-100 text-gray-800'}`}>
                                                                                {i18n.t(`fulfillment_errors.error_status.${round.error_status}`, { defaultValue: round.error_status })}
                                                                            </span>
                                                                            {round.retry_count > 0 && (
                                                                                <span>{i18n.t('fulfillment_errors.history.attempt', { n: round.retry_count })}</span>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </dd>
                                    </div>
                                )}
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.shipping_information')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.name')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.first_name} {data.shipping?.last_name}{ data.shipping?.last_name && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.phone')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.phone}{ data.shipping?.phone && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.email')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.email}{ data.shipping?.email && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.address')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.address_1} {data.shipping?.address_2}{ data.shipping?.address_2 && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.country')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.country}{ data.shipping?.country && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.state')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.state}{ data.shipping?.state && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.city')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.city}{ data.shipping?.city && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.zip_code')}:</td>
                                                    <td className='text-gray-500'> {data.shipping?.zip_code}{ data.shipping?.zip_code && <br/>} </td>
                                                </tr>
                                            </tbody>

                                        </table>
                                        {/* {data.shipping?.first_name}{ data.shipping?.first_name && <br/>}
                                        {data.shipping?.last_name}{ data.shipping?.last_name && <br/>}
                                        {data.shipping?.email}{ data.shipping?.email && <br/>}
                                        {data.shipping?.phone}{ data.shipping?.phone && <br/>}
                                        {data.shipping?.address_1}{ data.shipping?.address_1 && <br/>}
                                        {data.shipping?.address_2}{ data.shipping?.address_2 && <br/>}
                                        {data.shipping?.city}{ data?.shipping?.city && <br/>}
                                        {data.shipping?.state}{ data?.shipping?.state && <br/>}
                                        {data.shipping?.zip_code}{ data?.shipping?.zip_code && <br/>}
                                        {data.shipping?.country}{ data?.shipping?.country && <br/>} */}
                                    </dd>
                                </div>
                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                                         <dt className="text-base font-medium text-gray-500"><p>{i18n.t('orders.order_details.billing_information')}</p><a>{i18n.t('orders.order_details.edit_billing')}</a></dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                    <table>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.billing_name')}:</td>
                                                    <td className='text-gray-500'> {data.billing?.first_name} {data.billing?.last_name}{ data.billing?.last_name && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.billing_address')}:</td>
                                                    <td className='text-gray-500'> {data.billing?.address_1} {data.billing?.address_2}{ data.billing?.address_2 && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.billing_country')}:</td>
                                                    <td className='text-gray-500'> {data.billing?.country}{ data.billing?.country && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.billing_state')}:</td>
                                                    <td className='text-gray-500'> {data.billing?.state}{ data.billing?.state && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.billing_city')}:</td>
                                                    <td className='text-gray-500'> {data.billing?.city}{ data.billing?.city && <br/>} </td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.billing_zip_code')}:</td>
                                                    <td className='text-gray-500'> {data.billing?.zip_code}{ data.billing?.zip_code && <br/>} </td>
                                                </tr>


                                        </table>
                                        {/* {data.billing?.first_name}{ data.billing?.first_name && <br/>}
                                        {data.billing?.last_name}{ data.billing?.last_name && <br/>}
                                        {data.billing?.address_1}{ data.billing?.address_1 && <br/>}
                                        {data.billing?.address_2}{ data.billing?.address_2 && <br/>}
                                        {data.billing?.country}{ data.billing?.country && <br/>}
                                        {data.billing?.state}{ data.billing?.state && <br/>}
                                        {data.billing?.zip_code}{ data.billing?.zip_code && <br/>} */}
                                    </dd>

                                </div>

                                {
                                    data.picking_order && (
                                    <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                                                 <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.picking')}</dt>
                                        <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                            <table>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.picker')}:</td>
                                                    <td className='text-gray-500'>{data.picking_order.assigned_to?.first_name} {data.picking_order.assigned_to?.last_name}</td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.assigned')}:</td>
                                                    <td className='text-gray-500'><FormattedDate date={data.picking_order.assigned_at}/></td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.started')}:</td>
                                                    <td className='text-gray-500'><FormattedDate date={data.picking_order.tote_assigned_at}/></td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.tote')}:</td>
                                                    <td className='text-gray-500'>{data.picking_order.tote?.unique_code}</td>
                                                </tr>
                                                { shouldShowPickingList(data.status) &&
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.list')}:</td>
                                                    <td className='text-gray-500'><a className='pointer'
                                                    onClick={() => handleDownloadPickingList(data.picking_order.id)}>{i18n.t('orders.order_details.download')}</a> </td>
                                                </tr>
                                                }
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.completed')}:</td>
                                                    <td className='text-gray-500'><FormattedDate date={data.picking_order.completed_at}/></td>
                                                </tr>
                                            </table>
                                        </dd>
                                    </div>
                                    )
                                }

                                {user?.permissions?.beta_features && data?.id && <StockAllocationSection orderId={data.id} />}

                                {data?.id && <MissingStockDiagnosisSection orderId={data.id} orderStatus={data.status} orderUpdatedAt={data.updated_at} />}

                                {
                                    data.shipping_labels[0]?.created_by && (
                                    <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                                                 <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.packing')}</dt>
                                        <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                            <table>
                                                <tr>
                                                    <td> {i18n.t('orders.order_details.picker')}:</td>
                                                    <td className='text-gray-500'> {data.shipping_labels[0].created_by.first_name} {data.shipping_labels[0].created_by.last_name}</td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.printed_guide')}:</td>
                                                    <td className='text-gray-500'><FormattedDate date={data.shipping_date}/></td>
                                                </tr>
                                                <tr>
                                                    <td>{i18n.t('orders.order_details.packing_video')}:</td>
                                                    <td className='text-gray-500'>
                                                    {orderPackingProofVideoObjects && orderPackingProofVideoObjects.length > 0 ? (
                                                        orderPackingProofVideoObjects.map((obj, index) => {
                                                            return (
                                                                <React.Fragment key={obj.key}>
                                                                    {isDownloadingPackingProofVideo(obj) ? (
                                                                        <span>{i18n.t('orders.order_details.downloading')} ({(obj.size / (1024 * 1024)).toFixed(2)} {i18n.t('orders.order_details.mb')})</span> // Show progress indicator
                                                                    ) : (
                                                                        <a className="pointer" onClick={() => downloadProofVideo(obj)}>
                                                                            {obj.key} ({(obj.size / (1024 * 1024)).toFixed(2)} {i18n.t('orders.order_details.mb')})
                                                                        </a>
                                                                    )}
                                                                    {index < orderPackingProofVideoObjects.length - 1 && <br />}
                                                                </React.Fragment>
                                                            )
                                                        })
                                                    ) : (
                                                                                                                 <span className="text-gray-500">{i18n.t('orders.order_details.no_packing_evidence')}</span>
                                                    )}
                                                    </td>
                                                </tr>
                                            </table>
                                        </dd>
                                    </div>
                                    )
                                }
                                {removedUnits.length > 0 && (
                                    <div className="rounded even:bg-gray-100 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-base font-medium text-gray-500">
                                                                                         <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 text-xs font-medium">{i18n.t('orders.order_details.partial_shipment')}</span>
                                        </dt>
                                        <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                                                                         <h4 className="text-sm text-gray-700 mb-3">{i18n.t('orders.order_details.items_removed')}</h4>
                                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                                {groupedRemoved.map((row, idx) => (
                                                    <div key={`${row.product?.id || ''}-${row.barcode}-${idx}`} className="bg-white p-3 rounded border flex items-center justify-between">
                                                        <div>
                                                                                                                         <div className="text-sm font-semibold text-gray-900">{i18n.t('orders.order_details.upc')} {row.barcode}</div>
                                                             <div className="text-sm text-gray-600">{i18n.t('orders.order_details.name')} {row.product?.name}</div>
                                                             <div className="text-xs text-gray-500">{i18n.t('orders.order_details.sku')} {row.sku}</div>
                                                        </div>
                                                        <span className="ml-4 inline-flex items-center justify-center rounded-full bg-yellow-50 text-yellow-700 text-xs font-semibold px-2 py-1">x{row.count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </dd>
                                    </div>
                                )}

                                <div className="even:bg-gray-50 bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                                         <dt className="text-base font-medium text-gray-500">{i18n.t('orders.order_details.products')}</dt>
                                    <dd className="mt-1 text-base text-gray-900 sm:mt-0 sm:col-span-2">
                                        <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                            {data?.order_lines.map((order_line)=>
                                                <li className="pl-3 pr-4 py-3 text-md" key={order_line.id}>
                                                    <OrderLineItem
                                                        orderLine={order_line}
                                                    />
                                                </li>
                                            )}
                                        </ul>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                    </div>
            )}
        </>
    )
};
